import type { RouteLocationNormalized } from "vue-router";

import { clientStateManager, redactCredentialPath } from "@/services/error-services";
import { createDefaultLogger } from "@/types/console-logger";

const logger = createDefaultLogger();

/**
 * `router.onError` hook: a guard that threw, a route chunk that failed to download, or a throwing scrollBehavior.
 *
 * vue-router only prints these to the console while no onError listener exists, and RouterLink and Back/Forward
 * swallow the rejected navigation, so the first listener (RouteProgressBar hides itself on error) silenced every one
 * of them. Registered on the router itself so the report does not depend on a UI component being mounted. The logger
 * is a no-op in production, and a failed navigation leaves the member on the old page, so it goes to the client-error
 * channel as well.
 *
 * The paths are redacted: the sign-in-as token and the password-reset challenge and answer are route parameters, and
 * both routes load lazily, so a failed chunk on either would otherwise send the credential to the monitor.
 */
export function reportNavigationError( error: unknown, to: RouteLocationNormalized, from: RouteLocationNormalized ): void {
    const toPath = redactCredentialPath( to.path );
    const fromPath = redactCredentialPath( from.path );
    logger.error( `Navigation from '${ fromPath }' to '${ toPath }' failed`, error );
    clientStateManager.queueClientError( error instanceof Error ? error : new Error( String( error ) ), {
        navigation: 'failed',
        to: toPath,
        from: fromPath
    } );
}
